import { FC, useEffect, useState } from 'react';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { DataTable, DataTableFilterMeta, DataTableSelectionSingleChangeEvent } from 'primereact/datatable';
import { FilterMatchMode } from 'primereact/api';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { classNames } from 'primereact/utils';
import { Class } from '../../../models/Class';
import { TriStateCheckbox, TriStateCheckboxChangeEvent } from 'primereact/tristatecheckbox';

interface Props {
  visibleSelectClassDialog: boolean;
  setVisibleSelectClassDialog: (visible: boolean) => void;
  selectedClass?: Class | null;
  setSelectedClass?: (c: Class) => void;
  classesList: Class[] | null;
  multiple?: boolean;
  selectedClasses?: Class[] | null;
  setSelectedClasses?: (c: Class[]) => void;
}

const ClassesRosterSelectClassDialog: FC<Props> = ({
  visibleSelectClassDialog,
  setVisibleSelectClassDialog,
  selectedClass,
  setSelectedClass,
  classesList,
  multiple,
  selectedClasses,
  setSelectedClasses,
}) => {
  const [filters] = useState<DataTableFilterMeta>({
    ownerName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    section: { value: null, matchMode: FilterMatchMode.CONTAINS },
    'site.name': { value: null, matchMode: FilterMatchMode.CONTAINS },
    grades: { value: null, matchMode: FilterMatchMode.CONTAINS },
    isActive: { value: null, matchMode: FilterMatchMode.EQUALS },
  });

  const [preSelectedClasses, setPreSelectedClasses] = useState<Class[] | null>();

  const classesListData = classesList?.map((classItem: Class) => ({
    ...classItem,
    ownerName: `${classItem?.owner?.firstName} ${classItem?.owner?.lastName}`,
  }));

  const handleSelection = (e: DataTableSelectionSingleChangeEvent<Class[]>) => {
    if (e.value) {
      if (multiple) {
        setPreSelectedClasses && setPreSelectedClasses(e.value as unknown as Class[]);
      } else {
        setSelectedClass && setSelectedClass(e.value as Class);
        setSelectedClasses && setSelectedClasses([e.value]);
        setVisibleSelectClassDialog(false);
      }
    }
  };

  const activeBody = (e: Class) => (
    <i
      className={classNames('pi', {
        'true-icon pi-check-circle': e.isActive,
        'false-icon pi-times-circle': !e.isActive,
      })}
    ></i>
  );

  const isActiveFilterTemplate = (options: ColumnFilterElementTemplateOptions) => (
    <TriStateCheckbox
      key={options.index}
      value={options.value ?? ''}
      onChange={(e: TriStateCheckboxChangeEvent) => options.filterApplyCallback(e.value)}
    />
  );

  const footer = multiple ? (
    <div className="flex justify-content-end bg-blue-600">
      <Button
        label="Add to filter"
        aria-label="Add to filter"
        outlined
        rounded
        onClick={() => {
          setSelectedClasses &&
            setSelectedClasses(
              preSelectedClasses?.map(cl => {
                delete cl.ownerName;
                return cl;
              }) as Class[],
            );
          setVisibleSelectClassDialog(false);
        }}
      />
    </div>
  ) : null;

  useEffect(() => {
    setPreSelectedClasses(selectedClasses ? (selectedClasses as Class[]) : null);
  }, [selectedClasses]);

  return (
    <Dialog
      header="Select a Class"
      visible={visibleSelectClassDialog}
      className="nc_modal-wide"
      onHide={() => setVisibleSelectClassDialog(false)}
    >
      <DataTable
        footer={footer}
        selection={multiple ? preSelectedClasses : selectedClass!}
        onSelectionChange={(e: DataTableSelectionSingleChangeEvent<Class[]>) => {
          handleSelection(e);
        }}
        dataKey="id"
        paginator
        rows={5}
        rowsPerPageOptions={[5, 10, 25, 50]}
        value={classesListData}
        filters={filters}
        filterDisplay="row"
        globalFilterFields={['ownerName', 'name', 'section', 'site.name', 'grades']}
        emptyMessage="No classes found."
        stripedRows
      >
        <Column selectionMode={multiple ? 'multiple' : 'single'} headerStyle={{ width: '3rem' }}></Column>
        <Column field="ownerName" header="Owner" filter filterPlaceholder="Search by owner" />
        <Column field="name" header="Name" filter filterPlaceholder="Search by name" />
        <Column field="section" header="Section" filter filterPlaceholder="Search by section" />
        <Column field="site.name" header="Site" filter filterPlaceholder="Search by site" />
        <Column field="grades" header="Grade" filter filterPlaceholder="Search by grade" />
        <Column
          field="isActive"
          header="Active"
          dataType="boolean"
          style={{ minWidth: '6rem' }}
          body={activeBody}
          filter
          filterElement={isActiveFilterTemplate}
        />
      </DataTable>
    </Dialog>
  );
};

export default ClassesRosterSelectClassDialog;
